import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Radio, Tv, Ticket, Loader2, Gift, X, Users, MessageSquare,
  Send, SkipBack, Smile, Clock, Sparkles, Volume2, VolumeX
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import CinemaTheater from "../components/CinemaTheater";
import CinemaAudio from "../components/CinemaAudio";
import PreShowOverlay from "../components/PreShowOverlay";
import PremiereIntro from "../components/PremiereIntro";
import { useAuth } from "../lib/auth";

const REACTIONS = ["❤️", "🔥", "😂", "😮", "👏", "🎬"];
const THEATER_PREF_KEY = "cc_theater_mode";
const AMBIENT_PREF_KEY = "cc_theater_ambient";

function fmtCountdown(secs) {
  if (secs == null || secs < 0) return "";
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m.toString().padStart(2, "0")}m`;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function LivePlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paywall, setPaywall] = useState(null); // { price, currency, title, schedule_id, on_demand_unlock }
  const [buying, setBuying] = useState(false);
  const [next, setNext] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftMsg, setGiftMsg] = useState("");
  const [gifting, setGifting] = useState(false);

  // NEW — simulated-broadcast social layer
  const [viewers, setViewers] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatUnread, setChatUnread] = useState(0);
  const [floating, setFloating] = useState([]); // floating emoji reactions
  const [offAir, setOffAir] = useState(null);   // { next_program, starts_in_seconds }
  const [countdown, setCountdown] = useState(0);

  // Premiere Mode — fires once when a paid ticket session was just confirmed
  const { user } = useAuth();
  const [premiereShow, setPremiereShow] = useState(false);
  const [premiereTrigger, setPremiereTrigger] = useState(0);

  // Cinema-theater mode (persisted in localStorage; default ON)
  const [theaterMode, setTheaterMode] = useState(() => {
    if (typeof window === "undefined") return true;
    const raw = window.localStorage.getItem(THEATER_PREF_KEY);
    return raw === null ? true : raw === "1";
  });
  useEffect(() => {
    try { window.localStorage.setItem(THEATER_PREF_KEY, theaterMode ? "1" : "0"); } catch {}
  }, [theaterMode]);

  // Ambient audio (auditorium murmur + projector hum). Default OFF — needs a
  // user gesture to satisfy browser autoplay policy.
  const [ambient, setAmbient] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(AMBIENT_PREF_KEY) === "1";
  });
  useEffect(() => {
    try { window.localStorage.setItem(AMBIENT_PREF_KEY, ambient ? "1" : "0"); } catch {}
  }, [ambient]);

  // Persist the channel/show metadata for the marquee even when `data` is briefly
  // cleared (paywall / off-air branches set data=null).
  const [marqueeMeta, setMarqueeMeta] = useState({ channelName: "", showTitle: "" });
  useEffect(() => {
    if (data?.channel?.name || data?.video?.title) {
      setMarqueeMeta({
        channelName: data?.channel?.name || "",
        showTitle: data?.video?.title || "",
      });
    } else if (paywall?.title) {
      setMarqueeMeta((m) => ({
        channelName: paywall?.channel?.name || m.channelName,
        showTitle: paywall.title,
      }));
    } else if (offAir?.channel?.name || offAir?.next_program?.title) {
      setMarqueeMeta({
        channelName: offAir?.channel?.name || "",
        showTitle: offAir?.next_program?.title || "We'll be right back",
      });
    }
  }, [data, paywall, offAir]);

  const videoRef = useRef(null);
  const reloadRef = useRef(null);
  const wsRef = useRef(null);
  const chatBodyRef = useRef(null);
  const chatOpenRef = useRef(false);
  useEffect(() => { chatOpenRef.current = chatOpen; if (chatOpen) setChatUnread(0); }, [chatOpen]);

  const tune = async (startOver = false) => {
    try {
      const { data: d } = startOver
        ? await api.post(`/gcr/channels/${slug}/start-over`)
        : await api.get(`/gcr/channels/${slug}/play`);
      setData(d);
      setError(null); setPaywall(null); setOffAir(null);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Off-air right now";
      if (status === 402) {
        try {
          const { data: nowData } = await api.get(`/gcr/channels/${slug}/now`);
          setPaywall({
            title: nowData?.now?.title,
            schedule_id: nowData?.now?.schedule_id,
            price: nowData?.now?.ticket_price,
            currency: (nowData?.now?.ticket_currency || "usd").toUpperCase(),
            on_demand_unlock: nowData?.now?.on_demand_unlock,
            channel: nowData?.channel || null,
          });
          setError(null); setData(null); setOffAir(null);
        } catch { setError(msg); }
      } else if (status === 403) {
        setError(msg);
        setTimeout(() => nav("/pricing"), 1200);
      } else if (status === 404) {
        // Off-air → pull /now for next-up countdown card
        try {
          const { data: nowData } = await api.get(`/gcr/channels/${slug}/now`);
          if (nowData?.next_program) {
            setOffAir({ channel: nowData.channel, next_program: nowData.next_program });
            setError(null); setData(null); setPaywall(null);
          } else {
            setError(msg); setData(null); setOffAir(null);
          }
        } catch { setError(msg); setData(null); }
      } else {
        setError(msg); setData(null);
      }
    }
    try {
      const { data: now } = await api.get(`/gcr/channels/${slug}/now`);
      setNext(now.upcoming?.[0] || null);
      if (typeof now.viewers === "number") setViewers(now.viewers);
    } catch (e) {
      console.warn("[live] /now refresh failed", e?.response?.status);
    }
  };

  const buyTicket = async () => {
    if (!paywall?.schedule_id) return;
    setBuying(true);
    try {
      const { data: r } = await api.post("/gcr/tickets/checkout", {
        schedule_id: paywall.schedule_id,
        origin_url: window.location.origin,
      });
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBuying(false);
    }
  };

  const giftTicket = async () => {
    if (!paywall?.schedule_id) return;
    const email = giftEmail.trim().toLowerCase();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setGifting(true);
    try {
      const { data: r } = await api.post("/gcr/tickets/gift", {
        schedule_id: paywall.schedule_id,
        origin_url: window.location.origin,
        recipient_email: email,
        gift_message: giftMsg.trim() || undefined,
      });
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start gift checkout");
      setGifting(false);
    }
  };

  useEffect(() => { tune(); }, [slug]);

  // Stripe post-checkout: poll ticket status and then tune in
  useEffect(() => {
    const sid = sp.get("ticket_session_id");
    if (!sid) return;
    let attempts = 0;
    let t = null;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/gcr/tickets/status/${sid}`);
        if (data.status === "paid") {
          toast.success("Ticket confirmed — tuning in…");
          sp.delete("ticket_session_id"); setSp(sp, { replace: true });
          // Premiere Mode: ceremonial intro + applause swell, then tune in
          setPremiereShow(true);
          setPremiereTrigger((n) => n + 1);
          // Auto-enable Ambient so the premiere swell is actually audible
          setAmbient(true);
          setTimeout(() => tune(), 3200);
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired. Please try again.");
          sp.delete("ticket_session_id"); setSp(sp, { replace: true });
          return;
        }
      } catch (e) {
        console.warn("[live] ticket session poll failed", e?.response?.status);
      }
      if (attempts < 10) t = setTimeout(poll, 2000);
    };
    poll();
    return () => { if (t) clearTimeout(t); };
  }, []);

  // When current asset ends, re-tune to the next scheduled asset
  useEffect(() => {
    if (!data) return;
    if (reloadRef.current) clearTimeout(reloadRef.current);
    const ms = Math.max(2000, (data.ends_in_seconds + 2) * 1000);
    reloadRef.current = setTimeout(() => { tune(); }, ms);
    return () => clearTimeout(reloadRef.current);
  }, [data?.schedule_id]);

  // Off-air countdown tick
  useEffect(() => {
    if (!offAir?.next_program) { setCountdown(0); return; }
    const target = new Date(offAir.next_program.starts_at).getTime();
    const step = () => {
      const s = Math.max(0, Math.round((target - Date.now()) / 1000));
      setCountdown(s);
      if (s <= 0) tune();
    };
    step();
    const t = setInterval(step, 1000);
    return () => clearInterval(t);
  }, [offAir?.next_program?.schedule_id]);

  // ---- WebSocket: presence + chat + reactions (only when actively watching) ----
  useEffect(() => {
    if (!data) return;
    const token = localStorage.getItem("cc_token");
    if (!token) return;
    const wsUrl = `${API_BASE.replace(/^http/, "ws")}/gcr/ws/${slug}?token=${encodeURIComponent(token)}`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return; }
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let m; try { m = JSON.parse(ev.data); } catch { return; }
      if (m.type === "hello") {
        setViewers(m.viewers || 0);
        if (Array.isArray(m.chat_history)) setChatMessages(m.chat_history);
      } else if (m.type === "presence") {
        setViewers(m.viewers || 0);
      } else if (m.type === "chat") {
        setChatMessages((prev) => [...prev, m].slice(-200));
        if (!chatOpenRef.current) setChatUnread((u) => Math.min(99, u + 1));
      } else if (m.type === "reaction") {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const x = 8 + Math.random() * 72; // 8-80% from left
        setFloating((prev) => [...prev, { id, emoji: m.emoji, x }]);
        setTimeout(() => setFloating((prev) => prev.filter((e) => e.id !== id)), 2400);
      }
    };
    ws.onclose = () => { wsRef.current = null; };
    ws.onerror = () => { /* swallow */ };
    return () => { try { ws.close(); } catch {} wsRef.current = null; };
  }, [data?.schedule_id, slug]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [chatMessages.length, chatOpen]);

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      toast.error("Chat unavailable");
      return;
    }
    ws.send(JSON.stringify({ type: "chat", text }));
    setChatInput("");
  };

  const sendReaction = (emoji) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "reaction", emoji }));
    // Optimistic local float so the sender sees it immediately
    const id = `me-${Date.now()}`;
    const x = 8 + Math.random() * 72;
    setFloating((prev) => [...prev, { id, emoji, x }]);
    setTimeout(() => setFloating((prev) => prev.filter((e) => e.id !== id)), 2400);
  };

  const startOver = async () => {
    if (!data) return;
    toast.message("Rewinding to the start of this show…");
    await tune(true);
  };

  // Apply offset_seconds when the video metadata is ready
  const onLoaded = () => {
    const v = videoRef.current;
    if (v && data && data.offset_seconds > 1 && data.offset_seconds < (v.duration || 1e9)) {
      try { v.currentTime = data.offset_seconds; } catch {}
    }
  };

  const token = typeof window !== "undefined" ? localStorage.getItem("cc_token") : null;
  const src = useMemo(() => {
    if (!data) return null;
    let url = data.video?.stream_url || "";
    if (url.startsWith("/api/files/")) {
      const origin = API_BASE.replace(/\/api$/, "");
      url = `${origin}${url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
    }
    return url || null;
  }, [data, token]);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <button
        onClick={() => nav("/live")}
        data-testid="liveplayer-back"
        className="absolute top-6 left-6 z-20 btn-glass px-4 py-2 rounded flex items-center gap-2"
      >
        <ArrowLeft className="w-5 h-5" /> All channels
      </button>

      {data?.channel && (
        <div className="absolute top-6 right-6 z-20 flex items-center gap-2">
          <div className="flex items-center gap-3 bg-black/70 backdrop-blur px-3 py-1.5 rounded">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E50914] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E50914]"></span>
            </span>
            <span data-testid="liveplayer-channel-name" className="text-sm font-semibold">{data.channel.name}</span>
            <span className="text-[10px] uppercase tracking-widest text-[#E50914]">Live</span>
          </div>
          <div
            data-testid="liveplayer-viewer-count"
            className="flex items-center gap-1.5 bg-black/70 backdrop-blur px-3 py-1.5 rounded text-xs font-semibold"
            title="Viewers tuned in now"
          >
            <Users className="w-3.5 h-3.5 text-[#E50914]" />
            <span>{viewers.toLocaleString()}</span>
          </div>
          <button
            data-testid="liveplayer-theater-toggle"
            onClick={() => setTheaterMode((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition border ${
              theaterMode
                ? "bg-[#f6c34a]/15 border-[#f6c34a]/40 text-[#f6c34a]"
                : "bg-black/70 border-white/15 text-white/80 hover:text-white"
            }`}
            title={theaterMode ? "Switch to fullscreen mode" : "Switch to cinema mode"}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{theaterMode ? "Cinema" : "Plain"}</span>
          </button>
          {theaterMode && (
            <button
              data-testid="liveplayer-ambient-toggle"
              onClick={() => setAmbient((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition border ${
                ambient
                  ? "bg-[#f6c34a]/15 border-[#f6c34a]/40 text-[#f6c34a]"
                  : "bg-black/70 border-white/15 text-white/80 hover:text-white"
              }`}
              title={ambient ? "Mute auditorium ambience" : "Enable auditorium ambience"}
              aria-pressed={ambient}
            >
              {ambient ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              <span>{ambient ? "Ambient" : "Silent"}</span>
            </button>
          )}
        </div>
      )}

      <CinemaAudio
        enabled={theaterMode && ambient}
        mode={data && !error && !offAir && !paywall && src ? "open" : "closed"}
        premiereTrigger={premiereTrigger}
      />

      <PremiereIntro
        show={premiereShow}
        holderName={user?.name}
        showTitle={marqueeMeta.showTitle}
        onComplete={() => setPremiereShow(false)}
      />

      <CinemaTheater
        active={theaterMode}
        curtainsOpen={!!(data && !error && !offAir && !paywall && src) || !!paywall}
        marqueeTitle={marqueeMeta.channelName ? `Now Showing · ${marqueeMeta.channelName}` : "GCR Theaters"}
        marqueeSub={marqueeMeta.showTitle || (offAir ? "We'll be right back" : error ? "Off air" : "")}
        intermissionLabel={offAir ? "INTERMISSION" : error ? "OFF AIR" : "INTERMISSION"}
        intermissionContent={
          offAir && !paywall ? (
            <PreShowOverlay
              channelName={offAir.channel?.name}
              nextTitle={offAir.next_program?.title}
              nextPoster={offAir.next_program?.backdrop || offAir.next_program?.poster}
              seconds={countdown}
              onRefresh={() => tune()}
              onGuide={() => nav("/live")}
            />
          ) : null
        }
      >
        <div className="w-full h-full flex items-center justify-center bg-black">
        {paywall && (
          <div data-testid="ticket-paywall" className="text-center max-w-md px-6">
            <div className="inline-flex items-center gap-2 bg-[#E50914]/15 border border-[#E50914]/40 text-[#E50914] rounded-full px-3 py-1 text-xs uppercase tracking-widest font-bold mb-4">
              <Ticket className="w-3.5 h-3.5" /> Premium Event
            </div>
            <h2 className="text-3xl font-black mb-2">{paywall.title || "Premium Event"}</h2>
            <p className="text-gray-400 text-sm mb-6">
              This is a ticketed live broadcast. Buy a ticket to tune in now
              {paywall.on_demand_unlock ? " and watch on-demand for 24 hours after." : "."}
            </p>
            <div className="text-5xl font-black mb-6">
              {paywall.currency} ${paywall.price?.toFixed(2)}
            </div>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                data-testid="ticket-buy-btn"
                onClick={buyTicket}
                disabled={buying}
                className="btn-red px-8 py-3 rounded font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {buying ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…</> : <><Ticket className="w-4 h-4" /> Buy ticket</>}
              </button>
              <button
                data-testid="ticket-gift-btn"
                onClick={() => setGiftOpen(true)}
                className="btn-glass px-6 py-3 rounded font-semibold flex items-center gap-2 border border-white/20"
              >
                <Gift className="w-4 h-4" /> Gift to a friend
              </button>
              <button onClick={() => nav("/live")} className="btn-glass px-6 py-3 rounded font-semibold">Back to guide</button>
            </div>
            <p className="text-[11px] text-gray-500 mt-6">Refundable until the show starts. Gifts auto-unlock in the recipient's account.</p>
          </div>
        )}

        {/* Off-air card is rendered as intermissionContent over the closed curtains
           (see <CinemaTheater intermissionContent={...} /> above). When Cinema
           Mode is disabled, we still want a visible card here. */}
        {offAir && !paywall && !theaterMode && (
          <div data-testid="liveplayer-offair" className="text-center max-w-xl px-6">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-bold mb-4">
              <Clock className="w-3.5 h-3.5" /> Station Break
            </div>
            <h2 className="text-4xl font-black mb-2">We'll be right back.</h2>
            <p className="text-gray-400 mb-8">
              {offAir.channel?.name} returns in <span className="text-white font-semibold">{fmtCountdown(countdown)}</span>
            </p>
            {offAir.next_program?.backdrop && (
              <div className="relative rounded-xl overflow-hidden border border-white/10 mb-5">
                <img src={offAir.next_program.backdrop} alt="" className="w-full h-52 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-[#E50914] font-bold mb-1">Next up</div>
                  <div className="text-xl font-black">{offAir.next_program.title}</div>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button data-testid="offair-retry-btn" onClick={() => tune()} className="btn-red px-6 py-2.5 rounded font-semibold">Refresh</button>
              <button onClick={() => nav("/live")} className="btn-glass px-6 py-2.5 rounded font-semibold">Channel guide</button>
            </div>
          </div>
        )}

        {error && !paywall && !offAir && (
          <div data-testid="liveplayer-error" className="text-center px-6 max-w-md">
            <Tv className="w-12 h-12 text-[#E50914] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{error.includes("subscription") ? "Subscription required" : "Channel off-air"}</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => tune()} className="btn-red px-6 py-2 rounded font-semibold">Retry</button>
              <button onClick={() => nav("/live")} className="btn-glass px-6 py-2 rounded font-semibold">Channel guide</button>
            </div>
          </div>
        )}
        {!error && !paywall && !offAir && src && (
          <video
            key={data.schedule_id + (data.start_over ? "-so" : "")}
            ref={videoRef}
            data-testid="liveplayer-video"
            src={src}
            controls
            autoPlay
            playsInline
            onLoadedMetadata={onLoaded}
            onError={() => setError("Stream temporarily unavailable")}
            className="w-full h-full object-contain"
          />
        )}
        </div>
      </CinemaTheater>

      {/* Floating emoji reactions layer */}
      <div className="pointer-events-none absolute inset-0 z-30">
        {floating.map((f) => (
          <span
            key={f.id}
            className="absolute bottom-24 text-4xl select-none"
            style={{
              left: `${f.x}%`,
              animation: "gcr-float-up 2.2s ease-out forwards",
              filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.6))",
            }}
          >
            {f.emoji}
          </span>
        ))}
      </div>

      {data?.video && !error && !offAir && (
        <div className="absolute bottom-24 left-6 right-6 z-20 max-w-3xl bg-black/60 backdrop-blur-xl rounded-lg p-5 border border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <Radio className="w-4 h-4 text-[#E50914]" />
            <span className="text-[11px] text-[#E50914] font-bold uppercase tracking-widest">On Now</span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold">{data.video.title}</h2>
              <p className="text-sm text-gray-300 mt-1 line-clamp-2">{data.video.description}</p>
              {next && (
                <p className="text-xs text-gray-500 mt-3">
                  Next: <span className="text-gray-300 font-semibold">{next.title}</span> · {new Date(next.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
            <button
              data-testid="liveplayer-startover-btn"
              onClick={startOver}
              className="btn-glass shrink-0 px-3 py-2 rounded text-xs font-semibold flex items-center gap-1.5 border border-white/15 hover:border-white/40"
              title="Rewind to the beginning of this show"
            >
              <SkipBack className="w-3.5 h-3.5" /> Start over
            </button>
          </div>
        </div>
      )}

      {/* Reaction launcher (bottom-right) */}
      {data && !offAir && !error && (
        <div data-testid="liveplayer-reactions" className="absolute bottom-6 right-6 z-30 flex items-center gap-1 bg-black/70 backdrop-blur border border-white/10 rounded-full px-2 py-1.5">
          {REACTIONS.map((emo) => (
            <button
              key={emo}
              onClick={() => sendReaction(emo)}
              data-testid={`reaction-btn-${emo}`}
              className="text-xl hover:scale-125 transition-transform px-1.5"
              aria-label={`React with ${emo}`}
            >
              {emo}
            </button>
          ))}
        </div>
      )}

      {/* Chat toggle (bottom-left, next to panel) */}
      {data && !offAir && !error && (
        <button
          data-testid="liveplayer-chat-toggle"
          onClick={() => setChatOpen((v) => !v)}
          className="absolute bottom-6 left-6 z-30 btn-glass px-4 py-2.5 rounded-full font-semibold flex items-center gap-2 border border-white/15"
        >
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm">{chatOpen ? "Close chat" : "Live chat"}</span>
          {!chatOpen && chatUnread > 0 && (
            <span className="ml-1 bg-[#E50914] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">
              {chatUnread}
            </span>
          )}
        </button>
      )}

      {/* Live chat drawer */}
      {data && !offAir && !error && chatOpen && (
        <aside
          data-testid="liveplayer-chat"
          className="fixed top-0 right-0 bottom-0 z-40 w-full sm:w-96 bg-black/90 backdrop-blur-xl border-l border-white/10 flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#E50914]" />
              <h3 className="font-semibold">Live chat</h3>
              <span className="text-xs text-gray-500">· {viewers} watching</span>
            </div>
            <button onClick={() => setChatOpen(false)} aria-label="Close chat" className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div ref={chatBodyRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {chatMessages.length === 0 && (
              <div className="text-xs text-gray-500 text-center mt-8">
                Be the first to say something. Chat resets when a new show starts.
              </div>
            )}
            {chatMessages.map((m, i) => (
              <div key={i} className="text-sm">
                <span className="text-[#E50914] font-semibold mr-2">{m.name}</span>
                <span className="text-gray-200">{m.text}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 px-3 py-3 flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value.slice(0, 280))}
              onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
              placeholder="Say something…"
              className="flex-1 bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-2 text-sm"
              maxLength={280}
            />
            <button
              data-testid="chat-send-btn"
              onClick={sendChat}
              disabled={!chatInput.trim()}
              className="btn-red px-3 py-2 rounded disabled:opacity-40"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="px-3 pb-3 flex items-center gap-1 border-t border-white/5">
            <Smile className="w-3.5 h-3.5 text-gray-500 mr-1" />
            {REACTIONS.map((emo) => (
              <button
                key={`c-${emo}`}
                onClick={() => sendReaction(emo)}
                className="text-lg hover:scale-125 transition-transform px-1"
                aria-label={`React ${emo}`}
              >
                {emo}
              </button>
            ))}
          </div>
        </aside>
      )}

      {giftOpen && paywall && (
        <div
          data-testid="gift-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          onClick={() => !gifting && setGiftOpen(false)}
        >
          <div
            className="relative w-full max-w-md bg-gradient-to-br from-[#1a0a0a] to-[#0a0a0a] border border-[#E50914]/40 rounded-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !gifting && setGiftOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-[#E50914]" />
              <h3 className="text-xl font-black">Gift this ticket</h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              We'll email them a link to unlock <span className="text-white font-semibold">{paywall.title}</span>. If they don't have an account yet, it auto-claims when they sign up with this email.
            </p>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Recipient email</label>
            <input
              data-testid="gift-email-input"
              type="email"
              value={giftEmail}
              onChange={(e) => setGiftEmail(e.target.value)}
              placeholder="friend@example.com"
              className="w-full bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-2 text-sm mb-3"
              disabled={gifting}
            />
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">Message (optional)</label>
            <textarea
              data-testid="gift-message-input"
              value={giftMsg}
              onChange={(e) => setGiftMsg(e.target.value.slice(0, 280))}
              placeholder="Enjoy the show on me!"
              rows={3}
              className="w-full bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-2 text-sm mb-1 resize-none"
              disabled={gifting}
            />
            <p className="text-[10px] text-gray-500 mb-5 text-right">{giftMsg.length}/280</p>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-black">
                {paywall.currency} ${paywall.price?.toFixed(2)}
              </div>
              <button
                data-testid="gift-confirm-btn"
                onClick={giftTicket}
                disabled={gifting || !giftEmail}
                className="btn-red px-6 py-2.5 rounded font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {gifting ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…</> : <><Gift className="w-4 h-4" /> Send gift</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes gcr-float-up {
          0%   { transform: translateY(0) scale(0.9); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(-60vh) scale(1.35) rotate(8deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
